import { useState, useEffect, useCallback } from 'react'
import type { User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { authReturnUrl } from '../lib/authReturn'
import { apiDelete } from '../api/client'
import { forgetAsked } from '../lib/signupEmailPrompt'
import { clearAccountCollectionCache } from './useCollection'
import { clearRetryIntents } from '../lib/copiesRetryIntents'
import { clearPendingPullsForUser } from '../lib/sealedPendingPulls'
import { clearCapabilityForUser } from '../lib/copiesCapability'
import { purgeUserCaches } from '../lib/serviceWorker'
import { removeLocalSession } from '../lib/localSession'
import { beginOwnerTransition, cancelOwnerTransition, endOwnerTransition, isAccountFenced, setCurrentOwner } from '../lib/accountChannel'
import { hasForeignAccountTransition } from '../lib/accountFence'
import { flushScope, draftKeysForScope } from '../components/decks/editor/editorSaveQueue'
import { clearTextDraftsForScope, textDraftKeysForScope } from '../components/decks/editor/textDraftStore'
import { adoptAccountLanguage, registerLanguagePersister, tc } from '../i18n'
import { decideAuthChange, withOwnSignOut, type AuthDecision } from './offlineAuth'
import { getOfflineAdapter, offlineRuntimeActive, offlineRuntimeStarted, runAccountTransition } from '../lib/offline/runtime'
import { getIdentity } from '../lib/offline/identity'
import { accountOwner } from '../lib/offline/identityMachine'
import { C3_REASON } from '../lib/offline/offline.codes'
import { i18nEnabled } from '../i18n/flag'
import type { Lang } from '../i18n/languages'

/**
 * Best-effort write of the interface language to the signed-in account's
 * user metadata (there is no preferences table; `saved_profiles` is the
 * public page). Registered with the i18n module, which calls it from
 * setLanguage() and logs any failure without showing it. Signed out, it does
 * nothing. One module-level function, so every mounted useAuth() registers
 * the same one.
 */
async function persistLanguageToAccount(lang: Lang): Promise<void> {
  if (!supabase) return
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) return
  const { error } = await supabase.auth.updateUser({ data: { interface_language: lang } })
  if (error) throw new Error(error.message)
}

// Users whose account language this page load has already adopted. Once per
// user per load: a later token refresh re-delivers the same user object, and
// re-adopting then would undo a switch whose account write had failed.
const adoptedLanguageFor = new Set<string>()

/**
 * Every localStorage key the app writes that's tied to the user's
 * identity or session. Centralizing this list avoids the
 * "deleteAccount removes some keys but misses others" bug class
 * (pre-2026-05-09 deleteAccount removed `cb_saved_decks` /
 * `cb_collection` / `cb_migration_done` - none of which are real
 * keys in the codebase, so the local cache was never actually
 * cleared on account deletion).
 */
const USER_LOCAL_STORAGE_KEYS = [
  'commander-bracket-saved-decks',
  // The pre-v2 collection blob and claim markers. The collection migration
  // keeps them untouched for 30 days so a reverted deploy still finds them;
  // the account is gone here, so they go now.
  'commander-bracket-collection',
  'commander-bracket-collection-claimed',
  'commander-bracket-collection-pending-sync',
  'commander-bracket-migrated',
  'cb-commander-names',
  'cb-commander-names-ts',
  'cb_archidekt_username',
  'cb-post-auth-dest',
] as const

interface DeleteAccountResponse {
  status: 'deleted' | 'partial'
  decks_deleted?: boolean
  collections_deleted?: boolean
  auth_deleted?: boolean
  apple_revocation?: { status: string; reason?: string }
}

/** What a partial deletion failed to remove (site:account.delete.part.<code>). */
export type PartialDeletionPart = 'saved_decks' | 'collections' | 'account_record' | 'apple_token'

// The English names of the parts, for the error's own `message` (logs and the
// `error` analytics event read it, so it stays English in every language).
// The modal renders the parts from the catalog instead.
const PART_ENGLISH: Record<PartialDeletionPart, string> = {
  saved_decks: 'saved decks',
  collections: 'collections',
  account_record: 'account record',
  apple_token: 'Apple token',
}

export class PartialDeletionError extends Error {
  /** Stable code for the render site (the message itself is English). */
  // eslint-disable-next-line i18next/no-literal-string -- code (deletion part, provider) or error class name
  readonly code = 'partial_deletion'
  response: DeleteAccountResponse
  /** The parts that could not be removed, as codes. Empty means "some data". */
  parts: PartialDeletionPart[]
  constructor(response: DeleteAccountResponse) {
    const failed: PartialDeletionPart[] = []
    // eslint-disable-next-line i18next/no-literal-string -- code (deletion part, provider) or error class name
    if (response.decks_deleted === false) failed.push('saved_decks')
    // eslint-disable-next-line i18next/no-literal-string -- code (deletion part, provider) or error class name
    if (response.collections_deleted === false) failed.push('collections')
    // eslint-disable-next-line i18next/no-literal-string -- code (deletion part, provider) or error class name
    if (response.auth_deleted === false) failed.push('account_record')
    // eslint-disable-next-line i18next/no-literal-string -- code (deletion part, provider) or error class name
    if (response.apple_revocation?.status === 'failed') failed.push('apple_token')
    // eslint-disable-next-line i18next/no-literal-string -- English error message (logs, analytics); the modal renders site:account.delete.partial
    const what = failed.length === 0 ? 'some data' : failed.map(p => PART_ENGLISH[p]).join(', ')
    super(
      // eslint-disable-next-line i18next/no-literal-string -- English error message (logs, analytics); the modal renders site:account.delete.partial
      `Account deletion partially failed: ${what} could not be removed. ` +
      // eslint-disable-next-line i18next/no-literal-string -- as above
      `Please retry, or contact support if the issue persists.`,
    )
    // eslint-disable-next-line i18next/no-literal-string -- code (deletion part, provider) or error class name
    this.name = 'PartialDeletionError'
    this.response = response
    this.parts = failed
  }
}

/**
 * deleteAccount refused: the live session is not the account the user
 * confirmed deleting (another tab signed in as someone else). Nothing was
 * sent. The modal renders site:account.delete.account_changed.
 */
export class AccountChangedError extends Error {
  // eslint-disable-next-line i18next/no-literal-string -- error code
  readonly code = 'account_changed'
  constructor() {
    // eslint-disable-next-line i18next/no-literal-string -- English error message (logs); the modal renders site:account.delete.account_changed
    super('The signed-in account changed before deletion; nothing was deleted.')
    // eslint-disable-next-line i18next/no-literal-string -- error class name
    this.name = 'AccountChangedError'
  }
}

/**
 * deleteAccount: the server deleted the account, but this browser could not
 * remove its session from storage (neither sign-out call nor the direct
 * removal took). The tab is signed out; the modal renders
 * site:account.delete.local_session_not_removed.
 */
export class LocalSessionNotRemovedError extends Error {
  // eslint-disable-next-line i18next/no-literal-string -- error code
  readonly code = 'local_session_not_removed'
  constructor() {
    // eslint-disable-next-line i18next/no-literal-string -- English error message (logs); the modal renders site:account.delete.local_session_not_removed
    super('Account deleted, but this browser could not remove its signed-in session.')
    // eslint-disable-next-line i18next/no-literal-string -- error class name
    this.name = 'LocalSessionNotRemovedError'
  }
}

/**
 * deleteAccount: the server deleted the account, but this browser could not
 * clear the account's saved collection copy. The modal renders
 * site:account.delete.local_not_cleared.
 */
export class LocalCacheNotClearedError extends Error {
  // eslint-disable-next-line i18next/no-literal-string -- error code
  readonly code = 'local_not_cleared'
  constructor() {
    // eslint-disable-next-line i18next/no-literal-string -- English error message (logs); the modal renders site:account.delete.local_not_cleared
    super('Account deleted, but this browser could not clear its collection copy.')
    // eslint-disable-next-line i18next/no-literal-string -- error class name
    this.name = 'LocalCacheNotClearedError'
  }
}

export type AuthResult = { error: string | null }

// Normalize Supabase's { error } response AND thrown network errors into
// one { error: string | null } shape the modal can render. Never throws.
async function toAuthResult(
  fn: () => Promise<{ error: { message: string } | null }>,
): Promise<AuthResult> {
  try {
    const { error } = await fn()
    return { error: error ? error.message : null }
  } catch (err) {
    return { error: err instanceof Error ? err.message : tc('auth.error.generic') }
  }
}

// Returned by every wrapper when the Supabase client was never constructed
// (env vars missing). Same guard shape as the existing methods, but the
// modal needs something to render instead of a silent no-op.
// A function, not a constant: the text is read in the active language.
const noClient = (): AuthResult => ({ error: tc('auth.error.unavailable') })

interface AuthState {
  user: User | null
  loading: boolean
  // `returnNonce` is optional on every method that builds a return URL. Only
  // the Watchtower teaser passes one (see authReturn.ts); omitted, the URL
  // is exactly what it always was.
  signInWithGoogle: (returnNonce?: string | null) => Promise<void>
  // The sign-in modal's methods. The V2 Google wrapper exists alongside the
  // original because the modal needs an error it can render, while the direct
  // Google CTAs (CollectionPage, DecksPage) keep calling signInWithGoogle.
  signInWithGoogleV2: (returnNonce?: string | null) => Promise<AuthResult>
  signInWithApple: (returnNonce?: string | null) => Promise<AuthResult>
  signInWithDiscord: (returnNonce?: string | null) => Promise<AuthResult>
  signInWithEmail: (email: string, captchaToken?: string, returnNonce?: string | null) => Promise<AuthResult>
  signInWithPassword: (email: string, password: string, captchaToken?: string) => Promise<AuthResult>
  signUpWithPassword: (email: string, password: string, captchaToken?: string, returnNonce?: string | null) => Promise<AuthResult>
  resetPassword: (email: string, captchaToken?: string) => Promise<AuthResult>
  updatePassword: (newPassword: string) => Promise<AuthResult>
  updateEmail: (newEmail: string) => Promise<AuthResult>
  // Flushes the deck editor's pending saves FIRST. With edits that could not
  // be saved it signs out only when `force` is set, and otherwise returns
  // {blocked: 'unsaved_edits'} so the caller can ask.
  signOut: (opts?: SignOutOptions) => Promise<SignOutResult>
  deleteAccount: () => Promise<void>
  /**
   * `user` is the offline owner's stored profile, not a live session
   * (offline past expiry, a refresh pending, or "Sign in again" after a
   * rejection; offline contract C2). Its data reads; writes queue or wait.
   */
  offlineUser: boolean
}

export interface SignOutOptions {
  force?: boolean
  /**
   * Offline contract C3 step 4: asked when unsynced changes exist ("N
   * changes are not saved yet and will be removed"); resolves true to sign
   * out and remove them. Without it, the registered app confirmation.
   */
  confirm?: (count: number) => Promise<boolean>
}

/**
 * signOut's result: blocked when unsaved deck edits would be left behind;
 * failed when this browser could not remove the session (the tab is signed
 * out anyway, but storage still holds it: the caller says so).
 */
export interface SignOutResult {
  blocked?: 'unsaved_edits'
  /** local_session: the stored session could not be removed; cleanup: the device's data could not be wiped (retried). */
  failed?: 'local_session' | 'cleanup'
  /** The user kept the account at the unsynced-changes warning (C3 step 4). */
  cancelled?: true
}

/**
 * The session is gone from THIS browser, verified by reading storage back
 * (src/lib/localSession.ts), whatever the auth server said. 'unknown' (no
 * auth-js internals, test doubles only) falls back to what signOut reported.
 */
async function sessionLeftStorage(reportedOk: boolean): Promise<boolean> {
  const removal = await removeLocalSession(supabase)
  if (removal === 'unknown') return reportedOk
  return removal === 'removed'
}

/** How long sign-out waits for pending deck edits to save. */
const SIGN_OUT_FLUSH_MS = 5000

// Every mounted useAuth() sees the same auth event; one purge per transition.
const transitionPurges = new Map<string, Promise<void>>()

// This document is COORDINATING an account transition (its own sign-out or
// account deletion is running). Only the coordinator fences peers and tells
// them to reload. A peer that merely hears Supabase's auth event for that
// transition (another tab removed the session) purges its own caches and
// says nothing: rebroadcasting under its own document id fenced and reloaded
// the coordinator mid-purge (a spurious reload, the deletion screen cut off).
let coordinatingDepth = 0

async function coordinating<T>(fn: () => Promise<T>): Promise<T> {
  coordinatingDepth += 1
  try {
    return await fn()
  } finally {
    coordinatingDepth -= 1
  }
}

/**
 * Whether this document announces the transition an auth event reports: its
 * own sign-out / deletion, or a change it saw first that no other tab is
 * already coordinating (a sign-in here). A SIGNED_OUT it did not cause, or
 * any change while a peer's transition fences this tab, is a peer's.
 */
function announcesTransition(event: string): boolean {
  if (coordinatingDepth > 0) return true
  // eslint-disable-next-line i18next/no-literal-string -- an auth event name
  if (event === 'SIGNED_OUT') return false
  return !isAccountFenced() && !hasForeignAccountTransition()
}

/**
 * Purge the departing account's caches, then tell peer tabs to reload. Call it
 * only once the local session is ALREADY removed or replaced (Supabase emits
 * its auth event after writing storage, and signOut() resolves after it), so
 * a peer that reloads never boots the departing session
 * (src/lib/accountChannel.ts). Never rejects.
 */
function purgeAccountTransition(from: string, to: string | null, announce = true): Promise<void> {
  const key = `${from}>${to ?? ''}`
  const existing = transitionPurges.get(key)
  if (existing) return existing
  if (!announce) {
    // A peer of another tab's transition: purge locally, tell nobody.
    const local = purgeUserCaches().then(() => undefined, () => undefined).finally(() => transitionPurges.delete(key))
    transitionPurges.set(key, local)
    return local
  }
  // Fence peers for the purge too (a no-op for those already fenced).
  beginOwnerTransition(from)
  const run = purgeUserCaches()
    .then(() => endOwnerTransition(from, to), () => endOwnerTransition(from, to))
    .finally(() => transitionPurges.delete(key))
  transitionPurges.set(key, run)
  return run
}

export function useAuth(): AuthState {
  const [user, setUser] = useState<User | null>(null)
  const [offlineUser, setOfflineUser] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!supabase) {
      setLoading(false)
      return
    }
    let disposed = false
    const applyDecision = (d: AuthDecision) => {
      if (disposed) return
      setCurrentOwner(d.user?.id ?? null)
      setUser(d.user)
      setOfflineUser(d.offline)
    }

    // Get initial session
    // A rejected read (network down, storage blocked) settles as signed out
    // rather than leaving `loading` true forever and the rejection unhandled;
    // onAuthStateChange below still delivers a session if one arrives.
    // With the offline runtime (offline contract C2), the device's owner is
    // read too: offline past the token's expiry the owner's stored data still
    // shows, never a signed-out page.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!offlineRuntimeStarted()) {
        setUser(session?.user ?? null)
        setLoading(false)
        return
      }
      void decideAuthChange(null, session?.user ?? null, session?.expires_at ?? null).then((d) => {
        applyDecision(d)
        if (!disposed) setLoading(false)
      })
    }, () => {
      if (!offlineRuntimeStarted()) {
        setLoading(false)
        return
      }
      void decideAuthChange(null, null, null).then((d) => {
        applyDecision(d)
        if (!disposed) setLoading(false)
      })
    })

    // Listen for auth changes. When the account on this browser goes away
    // (sign-out here or in another tab, or a switch to a different account),
    // Cache Storage loses everything the departing user could have left
    // there (src/lib/serviceWorker.ts purgeUserCaches), and the next user is
    // adopted only AFTER that finished: nothing of theirs renders (or caches
    // a card image) while the old account's entries are still being removed.
    // Peer tabs still showing the old account are then told to reload
    // (src/lib/accountChannel.ts). A token refresh for the same user is not
    // a transition and applies at once. Changes apply in arrival order.
    let lastUserId: string | null = null
    let pending: Promise<void> | null = null
    const apply = (next: User | null) => {
      if (disposed) return
      setCurrentOwner(next?.id ?? null)
      setUser(next)
      setOfflineUser(false)
    }
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const next = session?.user ?? null
      const from = lastUserId
      const to = next?.id ?? null
      const transition = !!from && from !== to
      // Decided NOW, when the event arrives (the coordinator's own action is still running).
      const announce = transition && announcesTransition(event)
      lastUserId = to
      if (offlineRuntimeStarted()) {
        // Offline contracts C2 / C3: the decision is made once for every
        // mounted useAuth() (src/hooks/offlineAuth.ts). C3 itself purges the
        // departing owner's caches and tells peers; without IndexedDB the
        // runtime is inactive and the W1 purge below still runs.
        const expiresAt = session?.expires_at ?? null
        const step: Promise<void> = (pending ?? Promise.resolve())
          .then(async () => {
            const d = await decideAuthChange(from, next, expiresAt, { event: true })
            if (transition && from && !offlineRuntimeActive()) await purgeAccountTransition(from, to, announce)
            applyDecision(d)
          })
          .catch(() => apply(next))
        pending = step
        void step.finally(() => { if (pending === step) pending = null })
        return
      }
      if (!transition && !pending) {
        apply(next)
        return
      }
      const step: Promise<void> = (pending ?? Promise.resolve())
        .then(async () => {
          if (!transition) return
          await purgeAccountTransition(from, to, announce)
        })
        .then(() => apply(next), () => apply(next))
      pending = step
      void step.finally(() => { if (pending === step) pending = null })
    })

    return () => {
      disposed = true
      subscription.unsubscribe()
    }
  }, [])

  // Interface language (Phase 3 localization). Both effects are inert with
  // VITE_I18N off: no registration, no metadata read, no write.
  useEffect(() => {
    if (!i18nEnabled()) return
    return registerLanguagePersister(persistLanguageToAccount)
  }, [])

  useEffect(() => {
    if (!i18nEnabled() || !user || adoptedLanguageFor.has(user.id)) return
    adoptedLanguageFor.add(user.id)
    adoptAccountLanguage(user.user_metadata?.interface_language)
  }, [user])

  const signInWithGoogle = useCallback(async (returnNonce?: string | null) => {
    if (!supabase) return
    await supabase.auth.signInWithOAuth({
      // eslint-disable-next-line i18next/no-literal-string -- code (deletion part, provider) or error class name
      provider: 'google',
      options: {
        redirectTo: authReturnUrl(window.location, returnNonce),
      },
    })
  }, [])

  // --- Modal wrappers. Every one returns { error } instead of swallowing
  // the failure, because the modal that calls them has to say what went
  // wrong. captchaToken rides options.captchaToken on the methods a future
  // Supabase CAPTCHA enforcement would gate (the three email-sending
  // actions plus the password grant); when it is undefined the option is
  // OMITTED entirely, so the no-Turnstile build sends today's exact payload.
  const signInWithGoogleV2 = useCallback(async (returnNonce?: string | null): Promise<AuthResult> => {
    if (!supabase) return noClient()
    const client = supabase
    return toAuthResult(() => client.auth.signInWithOAuth({
      // eslint-disable-next-line i18next/no-literal-string -- code (deletion part, provider) or error class name
      provider: 'google',
      options: { redirectTo: authReturnUrl(window.location, returnNonce) },
    }))
  }, [])

  const signInWithApple = useCallback(async (returnNonce?: string | null): Promise<AuthResult> => {
    if (!supabase) return noClient()
    const client = supabase
    return toAuthResult(() => client.auth.signInWithOAuth({
      // eslint-disable-next-line i18next/no-literal-string -- code (deletion part, provider) or error class name
      provider: 'apple',
      options: { redirectTo: authReturnUrl(window.location, returnNonce) },
    }))
  }, [])

  const signInWithDiscord = useCallback(async (returnNonce?: string | null): Promise<AuthResult> => {
    if (!supabase) return noClient()
    const client = supabase
    return toAuthResult(() => client.auth.signInWithOAuth({
      // eslint-disable-next-line i18next/no-literal-string -- code (deletion part, provider) or error class name
      provider: 'discord',
      options: { redirectTo: authReturnUrl(window.location, returnNonce) },
    }))
  }, [])

  const signInWithEmail = useCallback(async (email: string, captchaToken?: string, returnNonce?: string | null): Promise<AuthResult> => {
    if (!supabase) return noClient()
    const client = supabase
    const options: { emailRedirectTo: string; captchaToken?: string } = {
      emailRedirectTo: authReturnUrl(window.location, returnNonce),
    }
    if (captchaToken) options.captchaToken = captchaToken
    return toAuthResult(() => client.auth.signInWithOtp({ email, options }))
  }, [])

  const signInWithPassword = useCallback(async (
    email: string,
    password: string,
    captchaToken?: string,
  ): Promise<AuthResult> => {
    if (!supabase) return noClient()
    const client = supabase
    // No token means no options key at all: the password grant takes
    // credentials only until CAPTCHA enforcement is switched on.
    if (!captchaToken) {
      return toAuthResult(() => client.auth.signInWithPassword({ email, password }))
    }
    return toAuthResult(() => client.auth.signInWithPassword({
      email,
      password,
      options: { captchaToken },
    }))
  }, [])

  const signUpWithPassword = useCallback(async (
    email: string,
    password: string,
    captchaToken?: string,
    returnNonce?: string | null,
  ): Promise<AuthResult> => {
    if (!supabase) return noClient()
    const client = supabase
    const options: { emailRedirectTo: string; captchaToken?: string } = {
      emailRedirectTo: authReturnUrl(window.location, returnNonce),
    }
    if (captchaToken) options.captchaToken = captchaToken
    return toAuthResult(() => client.auth.signUp({ email, password, options }))
  }, [])

  const resetPassword = useCallback(async (email: string, captchaToken?: string): Promise<AuthResult> => {
    if (!supabase) return noClient()
    const client = supabase
    // Deliberately the ORIGIN, not authReturnUrl(). Every other method here
    // returns you to the page you left, but a recovery link is not a return
    // trip - it ARMS SetPasswordModal, whose arming checks were themselves a
    // Codex blocker (recovery-hash phishing, 2026-08-15). Landing recovery
    // somewhere new buys nothing and would have to re-argue that review.
    const opts: { redirectTo: string; captchaToken?: string } = {
      redirectTo: window.location.origin,
    }
    if (captchaToken) opts.captchaToken = captchaToken
    return toAuthResult(() => client.auth.resetPasswordForEmail(email, opts))
  }, [])

  const updatePassword = useCallback(async (newPassword: string): Promise<AuthResult> => {
    if (!supabase) return noClient()
    const client = supabase
    return toAuthResult(() => client.auth.updateUser({ password: newPassword }))
  }, [])

  const updateEmail = useCallback(async (newEmail: string): Promise<AuthResult> => {
    if (!supabase) return noClient()
    const client = supabase
    // With secure email change ON (see plan Task 0 Check 2), Supabase sends
    // confirmation links to BOTH the old and the new address. The redirect
    // lands the user back on /account so the pending banner is in view.
    return toAuthResult(() => client.auth.updateUser(
      { email: newEmail },
      { emailRedirectTo: `${window.location.origin}/account` },
    ))
  }, [])

  const signOut = useCallback((opts?: SignOutOptions): Promise<SignOutResult> => coordinating(async (): Promise<SignOutResult> => {
    if (!supabase) return {}
    // Offline contract C3: an account that owns this device's offline data
    // signs out through the one transition protocol (fence, stop writers,
    // count unsynced changes and ask, tombstone, local sign-out, wipe every
    // owner byte, tell peers). Works offline. Without the runtime (no
    // IndexedDB) the sign-out below is unchanged.
    if (user && offlineRuntimeActive() && getIdentity().offlineOwner === accountOwner(user.id)) {
      // The deck editor's live saves get their chance first (bounded); what
      // cannot land is counted and the user is asked before it is removed.
      if (!opts?.force) await flushScope(user.id, { timeoutMs: SIGN_OUT_FLUSH_MS })
      const confirm = opts?.confirm
      const out = await withOwnSignOut(() => runAccountTransition(
        { reason: C3_REASON.signOut },
        confirm ? { confirm: (n: number) => confirm(n) } : {},
      ))
      // eslint-disable-next-line i18next/no-literal-string -- an outcome code
      if (out.outcome === 'cancelled') return { cancelled: true }
      setCurrentOwner(null)
      setUser(null)
      setOfflineUser(false)
      // Committed but not finished (findings 12, 13): the device stays
      // blocked until recovery verifies the session removal and the wipe;
      // the caller says so instead of "Signed out".
      // eslint-disable-next-line i18next/no-literal-string -- result codes
      if (out.outcome === 'incomplete') return { failed: out.sessionRemoved ? 'cleanup' : 'local_session' }
      return {}
    }
    // The deck editor saves each edit a moment after it is made. Write what is
    // pending BEFORE the session is revoked: afterwards the writes would fail
    // as signed out. Edits that still cannot land stay in this user's
    // on-device draft either way; the caller decides whether to leave them.
    if (user && !opts?.force) {
      const { ok } = await flushScope(user.id, { timeoutMs: SIGN_OUT_FLUSH_MS })
      // eslint-disable-next-line i18next/no-literal-string -- a result code
      if (!ok) return { blocked: 'unsaved_edits' }
    }
    const departing = user?.id ?? null
    // This browser only. Supabase's default scope is 'global', which revoked
    // every session the account holds: the iPhone app's refresh token died
    // and its cloud decks stopped loading until the user signed in again.
    // Peers still showing this account hold their requests from here until
    // the session is gone (then they reload), and a tab that boots meanwhile
    // waits instead of reading it (src/lib/accountChannel.ts).
    if (departing) beginOwnerTransition(departing)
    // eslint-disable-next-line i18next/no-literal-string -- a Supabase scope
    const out = await supabase.auth.signOut({ scope: 'local' })
    // Even scope 'local' calls the auth server first and, offline, keeps the
    // stored session. Remove it here regardless and verify it is gone before
    // any peer is told to reload.
    const removed = await sessionLeftStorage(!out?.error)
    if (!removed) {
      if (departing) cancelOwnerTransition(departing)
      // This tab is signed out anyway; the caller tells the user storage
      // still holds the session.
      setCurrentOwner(null)
      setUser(null)
      // eslint-disable-next-line i18next/no-literal-string -- a result code
      return { failed: 'local_session' }
    }
    // Text mode's unapplied text (textDraftStore) mirrors the save queue's
    // drafts: on the FORCED sign-out (the user confirmed leaving unsaved
    // edits) it stays on this device, user-scoped and only ever offered to
    // that user when they sign back in, exactly as "They stay saved on this
    // device" promises for the queue's drafts. On a clean sign-out (nothing
    // pending) it is cleared once the session is actually revoked.
    if (departing && !opts?.force) clearTextDraftsForScope(departing)
    // Kept per-copy Retry intents never outlive the session they were made in.
    if (departing) clearRetryIntents({ userId: departing })
    // Nor does a per-copy capability verdict (forbidden, downgraded): the next
    // session re-probes from scratch.
    if (departing) clearCapabilityForUser(departing)
    // Nor anything user-derived in Cache Storage (the legacy worker cache held
    // Supabase REST responses; the card image cache shows what this user had
    // on screen). Never throws.
    if (departing) await purgeAccountTransition(departing, null)
    return {}
  }), [user])

  const deleteAccount = useCallback((): Promise<void> => coordinating(async (): Promise<void> => {
    if (!supabase || !user) return
    // The account the user CONFIRMED deleting is the one this screen shows.
    // The live session must be that same account, or nothing is sent: another
    // tab may have signed in as someone else since this screen rendered, and
    // deleting whoever the session holds NOW would delete the wrong account.
    const intendedUserId = user.id

    const { data: { session } } = await supabase.auth.getSession()
    if (!session || session.user?.id !== intendedUserId) throw new AccountChangedError()

    // Offline contract C3 (reason delete_account): the server deletes first
    // (between the count and the tombstone); a partial deletion leaves local
    // state untouched; then every owner byte goes. The account page's own
    // confirmation already showed the unsynced count.
    const adapter = getOfflineAdapter()
    if (adapter && offlineRuntimeActive() && getIdentity().offlineOwner === accountOwner(intendedUserId)) {
      let partial: DeleteAccountResponse | null = null
      const out = await withOwnSignOut(() => runAccountTransition({ reason: C3_REASON.deleteAccount }, {
        confirm: async () => true,
        serverDelete: async () => {
          const response = await apiDelete<DeleteAccountResponse>('/auth/delete-account', { bearerToken: session.access_token })
          // eslint-disable-next-line i18next/no-literal-string -- a response status code
          if (response.status === 'partial') {
            partial = response
            // eslint-disable-next-line i18next/no-literal-string -- an outcome code
            return 'partial'
          }
          // eslint-disable-next-line i18next/no-literal-string -- an outcome code
          return 'deleted'
        },
        wipeExtra: (owner: string) => adapter.wipeExtra(owner, C3_REASON.deleteAccount),
      }))
      if (out.outcome === 'server_partial' && partial) throw new PartialDeletionError(partial)
      forgetAsked(intendedUserId)
      setCurrentOwner(null)
      setUser(null)
      setOfflineUser(false)
      if (out.outcome === 'incomplete') {
        if (!out.sessionRemoved) throw new LocalSessionNotRemovedError()
        throw new LocalCacheNotClearedError()
      }
      return
    }

    // Backend deletion runs first while the bearer token is still valid. The
    // API endpoint validates the token, then deletes saved_decks,
    // saved_collections, and the auth user (when SUPABASE_SERVICE_ROLE_KEY is
    // configured). On partial failure we throw without clearing local
    // storage or signing the user out - they may want to keep their session
    // and retry rather than be stranded with a half-deleted account.
    const response = await apiDelete<DeleteAccountResponse>('/auth/delete-account', {
      bearerToken: session.access_token,
    })

    if (response.status === 'partial') {
      throw new PartialDeletionError(response)
    }

    // Wipe every user-tied localStorage key. Pre-2026-05-09 this loop was
    // hardcoded to three key names that DIDN'T MATCH the actual keys
    // useSavedDecks / useCollection / pages write to, so local data
    // silently survived account deletion forever and resurfaced for the
    // next account that signed in on the same browser.
    // The server deleted the account: fence peer tabs before anything here
    // changes (src/lib/accountChannel.ts).
    beginOwnerTransition(intendedUserId)
    for (const key of USER_LOCAL_STORAGE_KEYS) {
      localStorage.removeItem(key)
    }
    // Deliberately NOT in that list. The email-prompt record is keyed by user
    // id and shared across accounts on this browser: removing the whole key
    // would re-expose any OTHER brand-new account here to a second prompt.
    forgetAsked(intendedUserId)
    // The deck editor's on-device drafts for this account.
    for (const key of draftKeysForScope(intendedUserId)) localStorage.removeItem(key)
    for (const key of textDraftKeysForScope(intendedUserId)) localStorage.removeItem(key)
    // The deleted account's collection cache, and ONLY that account's (the
    // guest cache and any other account's stay). Synchronous, and it clears
    // the in-memory store too: an owned-name sync still in flight must not
    // land during the awaited sign-out below and write it straight back.
    // The removal is verified; one retry, then it is reported, never claimed.
    const cacheCleared = clearAccountCollectionCache(intendedUserId) || clearAccountCollectionCache(intendedUserId)
    clearRetryIntents({ userId: intendedUserId })
    clearPendingPullsForUser(intendedUserId)
    clearCapabilityForUser(intendedUserId)
    // Caches go now; peer tabs are told to reload only after the session is
    // out of storage, below.
    await purgeUserCaches()

    // Sign out (this also invalidates the session). The account is gone on
    // the server, so if the server call fails the session still leaves THIS
    // browser: a peer must never reload into a deleted account's session.
    const out = await supabase.auth.signOut()
    let reportedOk = !out?.error
    if (!reportedOk) {
      // eslint-disable-next-line i18next/no-literal-string -- a Supabase scope
      const local = await supabase.auth.signOut({ scope: 'local' })
      reportedOk = !local?.error
    }
    // Both calls reach the auth server first and keep the stored session on
    // a network or retryable error. Remove it here regardless and VERIFY it
    // is gone before peers are told to reload.
    if (!(await sessionLeftStorage(reportedOk))) {
      cancelOwnerTransition(intendedUserId)
      setCurrentOwner(null)
      setUser(null)
      throw new LocalSessionNotRemovedError()
    }
    await purgeAccountTransition(intendedUserId, null)
    // The account IS deleted on the server; say that this browser still holds
    // its collection copy rather than reporting a clean success.
    if (!cacheCleared) throw new LocalCacheNotClearedError()
  }), [user])

  return {
    user,
    loading,
    offlineUser,
    signInWithGoogle,
    signInWithGoogleV2,
    signInWithApple,
    signInWithDiscord,
    signInWithEmail,
    signInWithPassword,
    signUpWithPassword,
    resetPassword,
    updatePassword,
    updateEmail,
    signOut,
    deleteAccount,
  }
}
